import type { MapClientStatus } from './schemas/map.js';
import type { ClientDirectoryItem, ClientRelation } from './schemas/clients.js';

/**
 * Lot AV « Page client » : logique pure partagée par le serveur (agrégation, règle des 24 h du résumé) et le front
 * (recherche de client de l'en-tête).
 */

// ------------------------------------------------------------------ Relation

/** Relation avec le client, d'après le statut de la Carte ; sans statut (fiche vide) : prospect. */
export function clientRelation(status: MapClientStatus | null | undefined): ClientRelation {
  if (status === 'subscribed' || status === 'no_subscription') return 'client';
  if (status === 'cancelled') return 'former';
  return 'prospect';
}

// ------------------------------------------------------------------ Résumé de l'assistant

/** Décision de Maxime : le résumé est refait à l'ouverture de la page s'il a plus de 24 h. */
export const CLIENT_SUMMARY_MAX_AGE_HOURS = 24;
/** Une rédaction lancée il y a moins de 3 minutes est « en cours » (pas de seconde rédaction en parallèle). */
export const CLIENT_SUMMARY_GENERATING_MINUTES = 3;

const toMs = (value: string | Date) => (value instanceof Date ? value.getTime() : Date.parse(value));

/** Le résumé est-il à refaire à l'ouverture (aucun, ou plus de 24 h) ? */
export function isSummaryStale(generatedAt: string | Date | null | undefined, now: Date): boolean {
  if (!generatedAt) return true;
  const at = toMs(generatedAt);
  if (Number.isNaN(at)) return true;
  return now.getTime() - at >= CLIENT_SUMMARY_MAX_AGE_HOURS * 3_600_000;
}

/** Une rédaction est-elle en cours (commencée il y a moins de 3 minutes) ? */
export function isSummaryGenerating(startedAt: string | Date | null | undefined, now: Date): boolean {
  if (!startedAt) return false;
  const at = toMs(startedAt);
  return !Number.isNaN(at) && now.getTime() - at < CLIENT_SUMMARY_GENERATING_MINUTES * 60_000;
}

// ------------------------------------------------------------------ Devis et factures

export interface MoneyTotalsInput {
  /** Lot AY : `subcontractedCents` (sous-traité plafonné) : le signé compte la part d'Evolusite. */
  quotes: readonly {
    status: 'pending' | 'accepted' | 'refused';
    amountCents: number;
    subcontractedCents?: number;
  }[];
  invoices: readonly { amountCents: number; outstandingCents: number }[];
}

/**
 * Totaux HT de la section « Devis et factures » : signé (part d'Evolusite, lot AY), sous-traité, en attente, facturé,
 * reste à encaisser.
 */
export function moneyTotals(input: MoneyTotalsInput): {
  signedCents: number;
  subcontractedCents: number;
  pendingCents: number;
  invoicedCents: number;
  outstandingCents: number;
} {
  const sum = (values: number[]) => values.reduce((s, v) => s + v, 0);
  const accepted = input.quotes.filter((q) => q.status === 'accepted');
  return {
    signedCents: sum(accepted.map((q) => q.amountCents - (q.subcontractedCents ?? 0))),
    subcontractedCents: sum(accepted.map((q) => q.subcontractedCents ?? 0)),
    pendingCents: sum(input.quotes.filter((q) => q.status === 'pending').map((q) => q.amountCents)),
    invoicedCents: sum(input.invoices.map((i) => i.amountCents)),
    outstandingCents: sum(input.invoices.map((i) => Math.max(0, i.outstandingCents))),
  };
}

// ------------------------------------------------------------------ Rendez-vous

/**
 * Rendez-vous à venir (qui finissent après `now`, du plus proche au plus lointain) et passés (du plus récent au
 * plus ancien), `limit` de chaque côté.
 */
export function splitMeetings<T extends { startAt: string; endAt: string }>(
  meetings: readonly T[],
  now: Date,
  limit = 10,
): { upcoming: T[]; past: T[] } {
  const nowMs = now.getTime();
  const upcoming = meetings
    .filter((m) => Date.parse(m.endAt) > nowMs)
    .sort((a, b) => Date.parse(a.startAt) - Date.parse(b.startAt))
    .slice(0, limit);
  const past = meetings
    .filter((m) => Date.parse(m.endAt) <= nowMs)
    .sort((a, b) => Date.parse(b.startAt) - Date.parse(a.startAt))
    .slice(0, limit);
  return { upcoming, past };
}

// ------------------------------------------------------------------ Recherche de l'en-tête

/** Texte comparable : minuscules, sans accents, espaces réduits, apostrophes et tirets en espaces. */
export function normalizeSearch(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[’'`\-_.,/()]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Clients qui correspondent à la saisie (nom, puis commune), du plus pertinent au moins pertinent : nom qui
 * commence par la saisie, puis un mot du nom, puis le nom qui la contient, puis la commune. Chaque mot de la
 * saisie doit se retrouver dans le nom ou la commune. Égalité : ordre alphabétique français.
 */
export function searchClients<T extends Pick<ClientDirectoryItem, 'name' | 'city'>>(
  items: readonly T[],
  query: string,
  limit = 8,
): T[] {
  const q = normalizeSearch(query);
  if (!q) return [];
  const words = q.split(' ');
  const scored: { item: T; score: number }[] = [];
  for (const item of items) {
    const name = normalizeSearch(item.name);
    const city = normalizeSearch(item.city ?? '');
    const haystack = `${name} ${city}`;
    if (!words.every((w) => haystack.includes(w))) continue;
    const nameWords = name.split(' ');
    let score = 4;
    if (name.startsWith(q)) score = 0;
    else if (nameWords.some((w) => w.startsWith(words[0]!))) score = 1;
    else if (name.includes(q)) score = 2;
    else if (city.startsWith(q)) score = 3;
    scored.push({ item, score });
  }
  return scored
    .sort((a, b) => a.score - b.score || a.item.name.localeCompare(b.item.name, 'fr'))
    .slice(0, limit)
    .map((s) => s.item);
}
